import { Divider, VStack } from '@chakra-ui/react'
import { useFlags } from '@missionlabs/feature-flags'
import { Input, useFormContext, ValidatedInput } from '@missionlabs/react'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAllClientAudioClips } from 'shared/hooks/useAllAudioClips'
import { Features, useHasClientFeature } from 'shared/hooks/useHasClientFeature'
import { isPhoneLinePlus } from 'shared/utils/env'

import { AdminAnonymousCallSetting } from '../../../../../shared/components/settings/AdminAnonymousCallSetting'
import { AssignedNumbers } from '../../../components/AssignedNumbers'
import { AddEditTeamFields } from './addEditTeamSchema'
import { MusicToggle } from './MusicToggle'
import { TeamLineBusy } from './TeamLineBusy'
import { TeamsOutOfHours } from './TeamOutOfHours'
import { TeamRoutingSettings } from './TeamRoutingSettings'
import { TeamUnanswered } from './TeamUnanswered'
import { Voicemail } from './Voicemail'

export const AddEditTeamDetails: FC = () => {
    const { t } = useTranslation()
    const { id: teamID = '' } = useParams()

    const { call_settings_line_busy, team_menu_voice_portal } = useFlags([
        'call_settings_line_busy',
        'team_menu_voice_portal'
    ])

    const {
        setValue,
        watch,
        trigger,
        formState: { isSubmitted }
    } = useFormContext<AddEditTeamFields>()
    const inHoursUnanswered = watch('callRoute.inHoursUnanswered')
    const busy = watch('callRoute.busy')

    const isAdvancedRoutingEnabled = useHasClientFeature(Features.advanced_routing)

    const audioClips = useAllClientAudioClips()

    const audioClipsWithDefault = useMemo(() => {
        return [{ label: t('Default'), value: '' }, ...audioClips]
    }, [audioClips, t])

    const resetInHoursUnanswered = useCallback(() => {
        setValue('unansweredRedirect', undefined)
        setValue('unansweredRedirectInternal', undefined)
        setValue('voicemail.customGreetingURL', '')
        setValue('voicemail.customGreetingNoMessageURL', '')
    }, [setValue])

    const handleChange = useCallback(
        (name: any, value: any) => {
            if (name === 'callRoute.inHoursUnanswered') {
                resetInHoursUnanswered()
            }

            setValue(name, value, { shouldDirty: true })

            // Routing changes which fields are required, so re-validate the whole form after submit
            if (isSubmitted) trigger()
        },
        [isSubmitted, resetInHoursUnanswered, setValue, trigger]
    )

    return (
        <VStack pt="24px" px="24px" spacing="24px" align="inherit" divider={<Divider />}>
            <ValidatedInput name="name" label={t('admin.teams.form.name')}>
                <Input placeholder={t('admin.teams.form.name_placeholder')} />
            </ValidatedInput>
            <AssignedNumbers numbers={watch('phoneNumbers')} />

            {team_menu_voice_portal.enabled && (
                <Voicemail
                    showMailbox={!!teamID}
                    extension={watch('extension')}
                    voicemailPinName="voicemailPin"
                    voicemailPinSet={watch('voicemailPinSet')}
                />
            )}

            <MusicToggle
                name="IVR.greetingEnabled"
                dropdownName="IVR.greeting"
                label={t('admin.teams.form.greetingEnabled')}
                subtext={t('admin.teams.form.greetingEnabled_subtext')}
                options={audioClips}
            />
            <MusicToggle
                name="waitMusic"
                dropdownName="customWaitMusicURL"
                label={t('admin.teams.form.waitMusic')}
                subtext={t('admin.teams.form.waitMusic_subtext')}
                options={audioClipsWithDefault}
            />
            <MusicToggle
                name="holdMusic"
                dropdownName="customHoldMusicURL"
                label={t('admin.teams.form.holdMusic')}
                subtext={t('admin.teams.form.holdMusic_subtext')}
                options={audioClipsWithDefault}
            />
            {(isAdvancedRoutingEnabled || isPhoneLinePlus) && <TeamRoutingSettings />}
            <TeamUnanswered
                inHoursUnanswered={inHoursUnanswered}
                audioClipsWithDefault={audioClipsWithDefault}
                handleChange={handleChange}
            />
            {call_settings_line_busy.enabled && (
                <TeamLineBusy
                    busy={busy}
                    audioClipsWithDefault={audioClipsWithDefault}
                    handleChange={handleChange}
                />
            )}

            <TeamsOutOfHours />
            <AdminAnonymousCallSetting
                onChange={setValue}
                anonymousCallSetting={watch('blockAnonymousCalls')}
            />
        </VStack>
    )
}
